/**
 * Authentication Hook
 *
 * Wrapper around Auth0's useAuth0 hook. All consumers use this hook —
 * never import @auth0/auth0-react directly.
 *
 * Provides a stable auth interface independent of SDK internals.
 *
 * ## Usage
 *
 * ```typescript
 * const { isAuthenticated, user, getAccessTokenSilently } = useAuth()
 *
 * // Use in API calls
 * const token = await getAccessTokenSilently()
 * fetch(url, { headers: { Authorization: `Bearer ${token}` } })
 * ```
 */

import { useAuth0 } from '@auth0/auth0-react'
import type { AuthContextValue } from './types'

export function useAuth(): AuthContextValue {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0()

  return {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect: () => loginWithRedirect(),
    logout: () =>
      auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
    getAccessTokenSilently: () => getAccessTokenSilently(),
  }
}
